import { formatPhone, normalizePhone } from '@fundline/shared/phone';
import { Link } from '@tanstack/react-router';
import { type FormEvent, useMemo, useState } from 'react';
import { Alert } from '../../components/ui/Alert.tsx';
import { Button } from '../../components/ui/Button.tsx';
import { SelectField, TextField } from '../../components/ui/Field.tsx';
import { Card, EmptyState, Spinner } from '../../components/ui/Layout.tsx';
import { TagChip } from '../../components/ui/TagChip.tsx';
import { useWorkspaceClaims } from '../../lib/auth.tsx';
import { MerchantCompliance } from '../compliance/MerchantCompliance.tsx';
import { MerchantSendingHours } from '../compliance/SendingWindow.tsx';
import { type Member, useMembersQuery } from '../members/use-members.ts';
import { type FieldDefinition, useFieldDefinitionsQuery } from '../settings/use-fields.ts';
import { type Tag, useTagsQuery } from '../settings/use-tags.ts';
import { MerchantFieldRow } from './MerchantFieldRow.tsx';
import { MerchantTimeline } from './MerchantTimeline.tsx';
import { type MerchantDetails, useMerchantMutations, useMerchantQuery } from './use-merchants.ts';

function messageOf(err: unknown): string {
  return err instanceof Error ? err.message : 'Something went wrong.';
}

export function MerchantPage({ merchantId }: { merchantId: string }) {
  const { workspaceId } = useWorkspaceClaims();
  const merchant = useMerchantQuery(workspaceId, merchantId);
  const fields = useFieldDefinitionsQuery(workspaceId);
  const tags = useTagsQuery(workspaceId);
  const members = useMembersQuery(workspaceId);

  const back = (
    <Link to="/merchants" className="callout font-medium text-ink-muted underline">
      ← All merchants
    </Link>
  );

  if (merchant.isPending || fields.isPending || tags.isPending || members.isPending) {
    return <Spinner label="Loading the merchant" />;
  }
  const error = merchant.error ?? fields.error ?? tags.error ?? members.error;
  if (error) {
    return (
      <div className="flex flex-col gap-4">
        {back}
        <Alert tone="error">{error.message}</Alert>
      </div>
    );
  }
  if (!merchant.data) {
    return (
      <div className="flex flex-col gap-4">
        {back}
        <EmptyState title="Merchant not found">
          It does not exist, or it is assigned to someone else.
        </EmptyState>
      </div>
    );
  }
  return (
    <div className="flex flex-col gap-5">
      {back}
      <MerchantProfile
        merchant={merchant.data}
        fields={fields.data ?? []}
        tags={tags.data ?? []}
        members={members.data ?? []}
      />
    </div>
  );
}

function MerchantProfile({
  merchant: m,
  fields,
  tags,
  members,
}: {
  merchant: MerchantDetails;
  fields: FieldDefinition[];
  tags: Tag[];
  members: Member[];
}) {
  const { workspaceId, role } = useWorkspaceClaims();
  const mutations = useMerchantMutations(workspaceId, m.id);
  const [actionError, setActionError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  const people = useMemo(
    () => new Map(members.map((x) => [x.userId, x.fullName || x.email])),
    [members],
  );
  const values = useMemo(() => new Map(m.values.map((v) => [v.fieldId, v])), [m.values]);
  const activeFields = fields.filter((f) => !f.archivedAt);
  const title = m.businessName ?? m.ownerName ?? formatPhone(m.phone);

  async function run(fn: () => Promise<unknown>, success: string): Promise<boolean> {
    setActionError(null);
    setNotice(null);
    try {
      await fn();
      setNotice(success);
      return true;
    } catch (err) {
      setActionError(messageOf(err));
      return false;
    }
  }

  return (
    <>
      <header className="flex flex-col gap-2">
        <h1 className="break-words title text-ink">{title}</h1>
        <p className="callout text-ink-muted">
          <span data-testid="merchant-phone">{formatPhone(m.phone)}</span>
          {m.state ? ` · ${m.state}` : ''}
          {` · Added ${new Date(m.createdAt).toLocaleDateString()}`}
        </p>
        <div>
          <Link
            to="/inbox/$merchantId"
            params={{ merchantId: m.id }}
            className="fl-btn fl-btn--primary focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-focus-ring"
          >
            Text
          </Link>
        </div>
        {m.archivedAt ? (
          <Alert tone="warning">
            Archived on {new Date(m.archivedAt).toLocaleDateString()}. Archived merchants are hidden
            from the list but nothing is deleted.
          </Alert>
        ) : null}
      </header>

      {actionError ? <Alert tone="error">{actionError}</Alert> : null}
      {notice ? <Alert tone="success">{notice}</Alert> : null}

      <div className="grid gap-5 lg:grid-cols-[minmax(0,1fr)_minmax(0,1fr)]">
        <div className="flex min-w-0 flex-col gap-5">
          <Card title="Assignment">
            <AssignmentPanel
              merchant={m}
              members={members}
              people={people}
              admin={role === 'admin'}
              busy={mutations.update.isPending}
              onReassign={(repId) =>
                run(
                  () => mutations.update.mutateAsync({ assigned_rep_id: repId }),
                  `Assigned to ${people.get(repId) ?? 'the new rep'}.`,
                )
              }
              onArchive={(archive) =>
                run(
                  () =>
                    mutations.update.mutateAsync({
                      archived_at: archive ? new Date().toISOString() : null,
                    }),
                  archive ? 'Merchant archived.' : 'Merchant restored.',
                )
              }
              onCorrectPhone={(phone) =>
                run(
                  () => mutations.update.mutateAsync({ phone }),
                  `Phone corrected to ${formatPhone(phone)}.`,
                )
              }
            />
          </Card>

          <Card title="Texting and consent">
            <MerchantCompliance merchant={m} people={people} />
          </Card>

          <Card title="Sending hours">
            <MerchantSendingHours workspaceId={workspaceId} merchant={m} admin={role === 'admin'} />
          </Card>

          <Card title="Tags">
            <TagsPanel
              tags={tags}
              applied={m.tagIds}
              busy={mutations.addTag.isPending || mutations.removeTag.isPending}
              onAdd={(tag) =>
                run(() => mutations.addTag.mutateAsync(tag.id), `Tagged ${tag.name}.`)
              }
              onRemove={(tag) =>
                run(() => mutations.removeTag.mutateAsync(tag.id), `Removed ${tag.name}.`)
              }
            />
          </Card>

          <Card title="Fields">
            {activeFields.length === 0 ? (
              <EmptyState title="No fields">An admin can add fields in Settings.</EmptyState>
            ) : (
              <dl className="flex flex-col divide-y divide-hairline">
                {activeFields.map((f) => (
                  <MerchantFieldRow
                    key={f.id}
                    field={f}
                    value={values.get(f.id)}
                    people={people}
                    onSave={async (value) => {
                      setNotice(null);
                      setActionError(null);
                      await mutations.setField.mutateAsync({ fieldId: f.id, value });
                    }}
                  />
                ))}
              </dl>
            )}
          </Card>
        </div>

        <div className="min-w-0">
          <Card title="Timeline">
            <MerchantTimeline
              workspaceId={workspaceId}
              merchantId={m.id}
              fields={fields}
              people={people}
            />
          </Card>
        </div>
      </div>
    </>
  );
}

function AssignmentPanel({
  merchant: m,
  members,
  people,
  admin,
  busy,
  onReassign,
  onArchive,
  onCorrectPhone,
}: {
  merchant: MerchantDetails;
  members: Member[];
  people: ReadonlyMap<string, string>;
  admin: boolean;
  busy: boolean;
  onReassign: (repId: string) => Promise<boolean>;
  onArchive: (archive: boolean) => Promise<boolean>;
  onCorrectPhone: (phone: string) => Promise<boolean>;
}) {
  const [repId, setRepId] = useState(m.assignedRepId);
  const [editingPhone, setEditingPhone] = useState(false);
  const [phone, setPhone] = useState('');
  const [phoneError, setPhoneError] = useState<string | null>(null);
  const active = members.filter((x) => x.status === 'active');
  const options = [
    ...active.map((x) => ({ value: x.userId, label: `${x.fullName || x.email} (${x.role})` })),
    // A disabled member may still hold merchants until an admin moves them.
    ...(active.some((x) => x.userId === m.assignedRepId)
      ? []
      : [
          {
            value: m.assignedRepId,
            label: `${people.get(m.assignedRepId) ?? 'Former member'} (no access)`,
          },
        ]),
  ];

  async function submitPhone(e: FormEvent) {
    e.preventDefault();
    const r = normalizePhone(phone);
    if (!r.ok) {
      setPhoneError(r.reason);
      return;
    }
    setPhoneError(null);
    if (await onCorrectPhone(r.e164)) setEditingPhone(false);
  }

  return (
    <div className="flex flex-col gap-4">
      {admin ? (
        <form
          className="flex flex-col gap-2 sm:flex-row sm:items-end"
          onSubmit={(e) => {
            e.preventDefault();
            void onReassign(repId);
          }}
        >
          <div className="flex-1">
            <SelectField
              label="Assigned to"
              value={repId}
              onChange={(e) => setRepId(e.target.value)}
              options={options}
            />
          </div>
          <Button
            type="submit"
            variant="secondary"
            busy={busy}
            disabled={repId === m.assignedRepId}
          >
            Reassign
          </Button>
        </form>
      ) : (
        <p className="callout text-ink-muted">
          Assigned to <span className="font-medium">{people.get(m.assignedRepId) ?? 'you'}</span>
        </p>
      )}

      {admin && !m.hasMessages ? (
        editingPhone ? (
          <form
            className="flex flex-col gap-2"
            onSubmit={(e) => void submitPhone(e)}
            onKeyDown={(e) => {
              if (e.key === 'Escape') setEditingPhone(false);
            }}
            noValidate
          >
            <TextField
              label="Correct phone number"
              type="tel"
              inputMode="tel"
              value={phone}
              onChange={(e) => setPhone(e.target.value)}
              error={phoneError}
              hint="Possible only until the first message. The change is recorded in the audit log."
              autoFocus
            />
            <div className="flex gap-2">
              <Button type="submit" busy={busy}>
                Save phone
              </Button>
              <Button variant="ghost" onClick={() => setEditingPhone(false)}>
                Cancel
              </Button>
            </div>
          </form>
        ) : (
          <div>
            <Button
              variant="ghost"
              onClick={() => {
                setPhone(formatPhone(m.phone));
                setPhoneError(null);
                setEditingPhone(true);
              }}
            >
              Correct phone number
            </Button>
          </div>
        )
      ) : null}

      <div>
        {m.archivedAt ? (
          <Button variant="secondary" busy={busy} onClick={() => void onArchive(false)}>
            Restore merchant
          </Button>
        ) : (
          <Button
            variant="secondary"
            disabled={busy}
            onClick={() => {
              if (
                window.confirm('Archive this merchant? It leaves the list; nothing is deleted.')
              ) {
                void onArchive(true);
              }
            }}
          >
            Archive merchant
          </Button>
        )}
      </div>
    </div>
  );
}

function TagsPanel({
  tags,
  applied,
  busy,
  onAdd,
  onRemove,
}: {
  tags: Tag[];
  applied: string[];
  busy: boolean;
  onAdd: (tag: Tag) => Promise<boolean>;
  onRemove: (tag: Tag) => Promise<boolean>;
}) {
  const [choice, setChoice] = useState('');
  const on = tags.filter((t) => applied.includes(t.id));
  const off = tags.filter((t) => !applied.includes(t.id));
  const selected = off.find((t) => t.id === choice);

  return (
    <div className="flex flex-col gap-3">
      {on.length === 0 ? (
        <p className="callout text-ink-muted">No tags yet.</p>
      ) : (
        <ul className="flex flex-wrap gap-2" aria-label="Applied tags">
          {on.map((t) => (
            <li key={t.id}>
              <TagChip
                name={t.name}
                color={t.color}
                action={
                  <button
                    type="button"
                    className="-mr-1 rounded-pill px-1 leading-none hover:bg-surface focus-visible:outline-2 focus-visible:outline-focus-ring"
                    aria-label={`Remove tag ${t.name}`}
                    disabled={busy}
                    onClick={() => void onRemove(t)}
                  >
                    ×
                  </button>
                }
              />
            </li>
          ))}
        </ul>
      )}
      {off.length > 0 ? (
        <form
          className="flex flex-col gap-2 sm:flex-row sm:items-end"
          onSubmit={(e) => {
            e.preventDefault();
            if (selected) void onAdd(selected).then(() => setChoice(''));
          }}
        >
          <div className="flex-1">
            <SelectField
              label="Add a tag"
              value={choice}
              onChange={(e) => setChoice(e.target.value)}
              options={[
                { value: '', label: 'Choose a tag' },
                ...off.map((t) => ({ value: t.id, label: t.name })),
              ]}
            />
          </div>
          <Button type="submit" variant="secondary" disabled={!selected} busy={busy}>
            Add tag
          </Button>
        </form>
      ) : null}
    </div>
  );
}
